import { Globe, Search, TrendingUp } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  CandidateCoverageResult,
  CoverageEnrichmentQuerySchema,
} from '@/services/types/coverage-types';

interface EnrichmentDetailsProps {
  searchQueries: CoverageEnrichmentQuerySchema | null;
  searchResults: any[] | null;
  candidate: CandidateCoverageResult | null;
}

export const EnrichmentDetails = ({
  searchQueries,
  searchResults,
  candidate,
}: EnrichmentDetailsProps) => {
  const hasEnrichmentData = searchQueries || searchResults || candidate;

  if (!hasEnrichmentData) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Search className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p>No enrichment data available</p>
        <p className="text-sm mt-2">
          This mention was not processed through the enrichment pipeline
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Queries */}
      {searchQueries &&
        searchQueries.queries &&
        searchQueries.queries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Queries ({searchQueries.queries.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {searchQueries.queries.map((query, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-gray-50 rounded-md"
                  >
                    <Badge variant="outline" className="text-xs">
                      {index + 1}
                    </Badge>
                    <span className="text-sm font-mono">{query}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Search Results */}
      {searchResults && searchResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Search Results ({searchResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {searchResults.slice(0, 5).map((result, index) => (
                <div key={index} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="text-xs">
                      Result {index + 1}
                    </Badge>
                  </div>
                  <div className="text-sm space-y-1">
                    {result.title && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Title:{' '}
                        </span>
                        <span>{result.title}</span>
                      </div>
                    )}
                    {result.url && (
                      <div>
                        <span className="font-medium text-gray-700">URL: </span>
                        <span className="font-mono text-xs text-blue-600 break-all">
                          {result.url}
                        </span>
                      </div>
                    )}
                    {result.description && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Description:{' '}
                        </span>
                        <span className="text-gray-600">
                          {result.description}
                        </span>
                      </div>
                    )}
                    {result.content && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Content:{' '}
                        </span>
                        <span className="text-gray-600">
                          {result.content.substring(0, 200)}...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {searchResults.length > 5 && (
                <div className="text-center py-2">
                  <Badge variant="outline" className="text-xs">
                    +{searchResults.length - 5} more results
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Candidate Coverage */}
      {candidate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Enriched Candidate Coverage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enrichment Summary */}
            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Enrichment Summary
              </h4>
              <p className="text-sm text-blue-800">
                {candidate.enrichment_summary}
              </p>
            </div>

            {/* Confidence Score */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Enrichment Confidence:</span>
                <span className="font-medium">
                  {(candidate.enrichment_confidence * 100).toFixed(1)}%
                </span>
              </div>
              <Progress
                value={candidate.enrichment_confidence * 100}
                className="h-2"
              />
            </div>

            {/* Candidate Coverage Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Basic Information
                </h4>

                {candidate.candidate_coverage.category && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Category:</span>
                    <Badge variant="secondary">
                      {candidate.candidate_coverage.category.replace('_', ' ')}
                    </Badge>
                  </div>
                )}

                {candidate.candidate_coverage.policy_type && (
                  <div className="space-y-2">
                    <span className="text-sm text-gray-500">Policy Type</span>
                    <p className="text-sm bg-gray-50 p-2 rounded">
                      {candidate.candidate_coverage.policy_type}
                    </p>
                  </div>
                )}

                {candidate.candidate_coverage.policy_identifiers &&
                  candidate.candidate_coverage.policy_identifiers.length >
                    0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500">
                        Policy Identifiers
                      </span>
                      <div className="space-y-1">
                        {candidate.candidate_coverage.policy_identifiers.map(
                          (identifier, index) => (
                            <p
                              key={index}
                              className="text-sm bg-gray-50 p-2 rounded"
                            >
                              {identifier.identifier_type}:{' '}
                              {identifier.identifier_value}
                            </p>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Carrier Information
                </h4>

                {candidate.candidate_coverage.carrier?.name && (
                  <div className="space-y-2">
                    <span className="text-sm text-gray-500">Carrier Name</span>
                    <p className="text-sm bg-gray-50 p-2 rounded">
                      {candidate.candidate_coverage.carrier.name}
                    </p>
                  </div>
                )}

                {candidate.candidate_coverage.carrier?.handles &&
                  candidate.candidate_coverage.carrier.handles.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500">
                        Carrier Contacts
                      </span>
                      <div className="space-y-1">
                        {candidate.candidate_coverage.carrier.handles.map(
                          (handle, index) => (
                            <p
                              key={index}
                              className="text-sm bg-gray-50 p-2 rounded"
                            >
                              {handle.handle_type}: {handle.value}
                            </p>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {candidate.candidate_coverage.named_insured &&
                  candidate.candidate_coverage.named_insured.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500">
                        Named Insured
                      </span>
                      <div className="space-y-1">
                        {candidate.candidate_coverage.named_insured.map(
                          (name, index) => (
                            <p
                              key={index}
                              className="text-sm bg-gray-50 p-2 rounded"
                            >
                              {name}
                            </p>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {candidate.candidate_coverage.benefits &&
                  candidate.candidate_coverage.benefits.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500">Benefits</span>
                      <div className="space-y-2">
                        {candidate.candidate_coverage.benefits.map(
                          (benefit, index) => (
                            <div key={index} className="bg-gray-50 p-2 rounded">
                              <div className="text-sm font-medium">
                                {benefit.benefit_type}
                              </div>
                              {benefit.limitations &&
                                Object.keys(benefit.limitations).length > 0 && (
                                  <div className="mt-1 space-y-1">
                                    {Object.entries(benefit.limitations).map(
                                      ([key, value]) => (
                                        <div
                                          key={key}
                                          className="text-xs text-gray-600"
                                        >
                                          {key}: ${value?.toLocaleString()}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {candidate.candidate_coverage.vehicles &&
                  candidate.candidate_coverage.vehicles.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500">Vehicles</span>
                      <div className="space-y-2">
                        {candidate.candidate_coverage.vehicles.map(
                          (vehicle, index) => (
                            <div key={index} className="bg-gray-50 p-2 rounded">
                              <div className="text-sm">
                                {vehicle.year && `${vehicle.year} `}
                                {vehicle.make && `${vehicle.make} `}
                                {vehicle.model}
                              </div>
                              {vehicle.vin && (
                                <div className="text-xs text-gray-600">
                                  VIN: {vehicle.vin}
                                </div>
                              )}
                              {vehicle.license_plate && (
                                <div className="text-xs text-gray-600">
                                  Plate: {vehicle.license_plate}
                                </div>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {candidate.candidate_coverage.claims &&
                  candidate.candidate_coverage.claims.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500">Claims</span>
                      <div className="space-y-2">
                        {candidate.candidate_coverage.claims.map(
                          (claim, index) => (
                            <div key={index} className="bg-gray-50 p-2 rounded">
                              {claim.claim_identifier && (
                                <div className="text-sm">
                                  <span className="text-gray-500">
                                    Claim ID:
                                  </span>{' '}
                                  {claim.claim_identifier}
                                </div>
                              )}
                              {claim.benefit_type && (
                                <div className="text-sm">
                                  <span className="text-gray-500">
                                    Benefit:
                                  </span>{' '}
                                  {claim.benefit_type}
                                </div>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {candidate.candidate_coverage.adjusters &&
                  candidate.candidate_coverage.adjusters.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm text-gray-500">Adjusters</span>
                      <div className="space-y-2">
                        {candidate.candidate_coverage.adjusters.map(
                          (adjuster, index) => (
                            <div key={index} className="bg-gray-50 p-2 rounded">
                              {adjuster.name && (
                                <div className="text-sm font-medium">
                                  {adjuster.name}
                                </div>
                              )}
                              {adjuster.benefit_type && (
                                <div className="text-sm text-gray-600">
                                  Assigned to: {adjuster.benefit_type}
                                </div>
                              )}
                              {adjuster.handles &&
                                adjuster.handles.length > 0 && (
                                  <div className="mt-1 space-y-1">
                                    {adjuster.handles.map(
                                      (handle, handleIndex) => (
                                        <div
                                          key={handleIndex}
                                          className="text-xs text-gray-600"
                                        >
                                          {handle.handle_type}: {handle.value}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Policy Details */}
            {candidate.candidate_coverage.policy_details &&
              candidate.candidate_coverage.policy_details.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Policy Details
                  </h4>
                  <div className="space-y-2">
                    {candidate.candidate_coverage.policy_details.map(
                      (detail, index) => (
                        <div
                          key={index}
                          className="flex justify-between text-sm"
                        >
                          <span className="text-gray-600">{detail.key}:</span>
                          <span className="font-medium">{detail.value}</span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
